import { useSeoMeta } from '@unhead/react';
import { useNostr } from '@/hooks/useNostr';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

interface VideoEvent {
  id: string;
  pubkey: string;
  content: string;
  tags: string[][];
}

function VideoCard({ event }: { event: VideoEvent }) {
  const titleTag = event.tags.find(t => t[0] === 'title');
  const title = titleTag ? titleTag[1] : 'Untitled Video';
  
  let thumbnail = '';
  const imetaTag = event.tags.find(t => t[0] === 'imeta');
  
  if (imetaTag) {
    // The imeta tag format: ["imeta", "url https://...", "m video/mp4", "image https://...", ...]
    for (let i = 1; i < imetaTag.length; i++) {
      const part = imetaTag[i];
      const spaceIndex = part.indexOf(' ');
      if (spaceIndex !== -1) {
        const key = part.substring(0, spaceIndex);
        const value = part.substring(spaceIndex + 1);
        if (key === 'image') {
          thumbnail = value;
          break;
        }
      }
    }
  }

  return (
    <Card className="overflow-hidden group cursor-pointer bg-card hover:shadow-xl transition-all duration-300">
      <div className="aspect-video relative w-full overflow-hidden bg-muted">
        {thumbnail ? (
          <img 
            src={thumbnail} 
            alt={title} 
            className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-110"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs italic bg-slate-200 dark:bg-slate-800">
            No thumbnail available
          </div>
        )}
      </div>
      <CardContent className="p-3">
        <h3 className="font-semibold text-sm line-clamp-2 text-card-foreground group-hover:text-primary transition-colors">
          {title}
        </h3>
      </CardContent>
    </Card>
  );
}

const Index = () => {
  const { nostr } = useNostr();

  useSeoMeta({
    title: 'Video Feed | Nostr',
    description: 'Latest video events (Kind 34235) from the Nostr network.',
  });

  const { data: events, isLoading, isError } = useQuery<VideoEvent[]>({
    queryKey: ['video-events-kind-34235'],
    queryFn: async () => {
      return await nostr.query([{ kinds: [34235], limit: 6 }]);
    },
    refetchInterval: 60000,
  });

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <main className="flex-grow container mx-auto px-4 py-12">
        <header className="mb-12 text-center">
          <h1 className="text-4xl font-extrabold tracking-tight mb-3">Latest Videos</h1>
          <p className="text-muted-foreground">Discover the latest content from Nostr (Kind 34235)</p>
        </header>

        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="space-y-3">
                <Skeleton className="aspect-video w-full rounded-xl" />
                <Skeleton className="h-5 w-3/4" />
              </div>
            ))}
          </div>
        ) : isError ? (
          <div className="py-12 text-center border-2 border-dashed rounded-xl">
            <p className="text-destructive">Error loading video feed. Please check your connection or relays.</p>
          </div>
        ) : (
          <>
            {events && events.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                {events.map((event) => (
                  <VideoCard key={event.id} event={event} />
                ))}
              </div>
            ) : (
              <div className="py-12 text-center border-2 border-dashed rounded-xl">
                <p className="text-muted-foreground">No videos found. This might be due to no active relays or no videos of this kind yet.</p>
              </div>
            )}
          </>
        )}
      </main>

      <footer className="py-8 text-center border-t">
        <p className="text-sm text-muted-foreground">
          Vibed with <a href="https://shakespeare.diy" className="text-primary hover:underline font-medium">Shakespeare</a>
        </p>
      </footer>
    </div>
  );
};

export default Index;
